import toast from 'react-hot-toast';
import {
  downloadEstimatePdfApi,
  downloadPdfApi,
  postBusinessEstimateApi,
  putBusiness,
  putBusinessEstimateApi,
} from '~/api';
import { ESTIMATE_LIST_ROUTE } from '~/constants/routes';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';
import { formSlice } from '.';
import { createAppAsyncThunk } from '../../createAppAsyncThunk';
import { serializeAndValidateFormData } from './helpers';
import { clearUserLimits } from '../userLimits';

interface SubmitFormResponseType {
  uuid: string;
}

interface PdfGenResponseType {
  pdf_url: string;
}

// Define the async thunk for submitting form data
export const submitFormData = createAppAsyncThunk(
  'estimateForm/submitFormData',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const { formData, isValid, errorMsg } = serializeAndValidateFormData(
      state.estimateForm.formData,
    );

    if (!isValid) {
      toast.error(errorMsg || 'Please fill required details');
    }

    const storeEstimateUuid = state.estimateForm.estimateId;
    const estimateUuid = formData.estimateUuid || storeEstimateUuid;

    try {
      const businessUuid = state.user.businessDetails.business_uuid;

      const promise = estimateUuid
        ? await putBusinessEstimateApi(businessUuid, estimateUuid, formData)
        : await postBusinessEstimateApi(businessUuid, formData);

      const response = await apiResponseHandler<SubmitFormResponseType>(promise);
      let uuid = '';

      if (response.type === ApiResponseEnum.Success) {
        uuid = response.data.uuid;
      } else {
        toast.error(
          response.error ?? 'An error occurred while saving your data, please try again later',
        );
        return;
      }
      dispatch(formSlice.actions.setEstimateId(uuid));

      // Invalidate user limits cache on new estimate creation
      if (!estimateUuid) {
        dispatch(clearUserLimits());
      }

      const { saveTermsAsDefault, saveNotesAsDefault } = state.estimateForm.formData;
      if (saveTermsAsDefault || saveNotesAsDefault) {
        const business = state.user.businessDetails;
        await putBusiness(business.business_uuid, {
          ...business,
          ...(saveTermsAsDefault && { terms: state.estimateForm.formData.terms }),
          ...(saveNotesAsDefault && { notes: state.estimateForm.formData.notes }),
        });
      }
    } catch (error) {
      toast.error('An error occurred while submitting your data, please try again later');
      console.error('Error submitting form data:', error);
    }
  },
);

export const submitFormDataAndDownloadPdf = createAppAsyncThunk(
  'estimateForm/submitFormDataAndDownloadPdf',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const { formData, isValid, errorMsg } = serializeAndValidateFormData(
      state.estimateForm.formData,
    );

    if (!isValid) {
      toast.error(errorMsg || 'Please fill required details');
    }

    const storeEstimateUuid = state.estimateForm.estimateId;
    const estimateUuid = formData.estimateUuid || storeEstimateUuid;

    try {
      const businessUuid = state.user.businessDetails.business_uuid;

      const submitPromise = estimateUuid
        ? await putBusinessEstimateApi(businessUuid, estimateUuid, formData)
        : await postBusinessEstimateApi(businessUuid, formData);

      const submitResponse = await apiResponseHandler<SubmitFormResponseType>(submitPromise);
      let estimateId = '';

      if (submitResponse.type === ApiResponseEnum.Success) {
        estimateId = submitResponse.data.uuid;
      } else {
        toast.error(
          submitResponse.error ??
            'An error occurred while saving your data, please try again later',
        );
        return;
      }

      // Invalidate user limits cache on new estimate creation
      if (!estimateUuid) {
        dispatch(clearUserLimits());
      }

      const { saveTermsAsDefault, saveNotesAsDefault } = state.estimateForm.formData;
      if (saveTermsAsDefault || saveNotesAsDefault) {
        const business = state.user.businessDetails;
        await putBusiness(business.business_uuid, {
          ...business,
          ...(saveTermsAsDefault && { terms: state.estimateForm.formData.terms }),
          ...(saveNotesAsDefault && { notes: state.estimateForm.formData.notes }),
        });
      }

      const promise = await downloadEstimatePdfApi(estimateId, {
        template_id: formData.template_id,
      });
      const response = await apiResponseHandler<PdfGenResponseType>(promise);

      if (response.type !== ApiResponseEnum.Success) {
        toast.error('An error occurred while generating your PDF, please try again later');
        return;
      }

      toast.success('Estimate saved successfully');
      window.location.href = `${ESTIMATE_LIST_ROUTE}?viewId=${estimateId}`;
    } catch (error) {
      toast.error('An error occurred while submitting your data, please try again later');
      console.error('Error submitting form data:', error);
    }
  },
);

export const downloadEstimatePdf = createAppAsyncThunk(
  'estimateForm/downloadEstimatePdf',
  async ({ templateId }: { templateId: string }, { getState }) => {
    const state = getState();
    const estimateId = state.estimateForm.estimateId as string;
    try {
      // Make call for PDF
      const promise = await downloadPdfApi(estimateId, {
        template_id: templateId,
      });
      const response = await apiResponseHandler<PdfGenResponseType>(promise);

      if (response.type !== ApiResponseEnum.Success) {
        toast.error('An error occurred while generating your PDF, please try again later');
        return;
      }

      toast.success('Estimate saved successfully');
      window.location.href = `${ESTIMATE_LIST_ROUTE}?viewId=${estimateId}`;
    } catch (error) {
      toast.error('An error occurred while downloading your pdf, please try again later');
      console.error('Error submitting downloading pdf:', error);
    }
  },
);
